import { useEffect, useMemo, useRef, useState } from 'react';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useDragControls, useMotionValue, useTransform } from 'framer-motion';
import AlternateEmailRounded from '@mui/icons-material/AlternateEmailRounded';
import AccessTimeRounded from '@mui/icons-material/AccessTimeRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import EmojiEventsRounded from '@mui/icons-material/EmojiEventsRounded';
import NotificationsRounded from '@mui/icons-material/NotificationsRounded';
import SettingsRounded from '@mui/icons-material/SettingsRounded';
import TaskAltRounded from '@mui/icons-material/TaskAltRounded';
import { useNotifications } from '../../context/useNotifications';
import { useGlobalActionLoading } from '../../context/useGlobalActionLoading';
import type { NotificationCategory, NotificationRecord } from '../../types/schema';
import { usePushBackHandler } from '../../context/BackStackContext';
import { useToast } from '../../context/useToast';
import { useNls } from '../../nls/nlsProvider';
import { ConfirmActionModal } from '../Common/ConfirmActionModal';
import { Button, Pressable } from '../Common';
import { NotificationUndoSnackbar, type UndoState } from '../Common/NotificationUndoSnackbar';
import type { DigestInnerItem, NotificationRouterHandle } from '../../services/notificationRouter';
import './MemberNotificationDrawer.css';

const MotionPressable = motion.create(Pressable);

type NotificationGroup = 'ACTIVITY' | 'AI' | 'REWARDS' | 'SYSTEM';
const CATEGORY_TO_GROUP: Record<NotificationCategory, NotificationGroup> = {
  MENTIONS: 'ACTIVITY',
  GROUP_CHAT: 'ACTIVITY',
  DUE: 'ACTIVITY',
  AI_FAILS: 'AI',
  AI_JOBS: 'AI',
  INGESTION: 'AI',
  GAMIFICATION: 'REWARDS',
  SUBSCRIPTION: 'SYSTEM',
  SECURITY: 'SYSTEM',
  TIPS: 'SYSTEM',
  UPDATES: 'SYSTEM',
  DIGEST: 'SYSTEM',
  EDU_ATTENDANCE: 'ACTIVITY',
  EDU_LEAVE: 'ACTIVITY',
  EDU_CLASSES: 'ACTIVITY',
  EDU_HOMEWORK: 'ACTIVITY',
  EDU_TODAY: 'ACTIVITY',
  EDU_REPORTS: 'ACTIVITY',
  EDU_FEES: 'ACTIVITY',
  EDU_BOOKINGS: 'ACTIVITY',
  EDU_PROGRESS: 'ACTIVITY',
  EDU_ANNOUNCEMENTS: 'ACTIVITY',
  EDU_MESSAGES: 'ACTIVITY',
  EDU_ADMISSIONS: 'ACTIVITY',
  EDU_EVENTS: 'ACTIVITY',
};
const GROUP_ORDER: NotificationGroup[] = ['ACTIVITY', 'AI', 'REWARDS', 'SYSTEM'];
const resolveGroup = (n: NotificationRecord): NotificationGroup => (n.category && n.category in CATEGORY_TO_GROUP ? CATEGORY_TO_GROUP[n.category] : 'ACTIVITY');
const formatRelative = (value: unknown) => {
  const ms =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Date.parse(value)
        : typeof value === 'object' && value && 'toDate' in (value as Record<string, unknown>)
          ? (value as { toDate: () => Date }).toDate().getTime()
          : NaN;
  const delta = Date.now() - ms;
  return Number.isNaN(delta)
    ? ''
    : delta < 60_000
      ? 'now'
      : delta < 3_600_000
        ? `${Math.floor(delta / 60_000)}m`
        : delta < 86_400_000
          ? `${Math.floor(delta / 3_600_000)}h`
          : `${Math.floor(delta / 86_400_000)}d`;
};
const GroupIcon = ({ group, category }: { group: NotificationGroup; category?: NotificationCategory }) =>
  category === 'DUE' ? (
    <AccessTimeRounded />
  ) : group === 'AI' ? (
    <AutoAwesomeRounded />
  ) : group === 'REWARDS' ? (
    <EmojiEventsRounded />
  ) : group === 'SYSTEM' ? (
    <NotificationsRounded />
  ) : (
    <AlternateEmailRounded />
  );
type Props = {
  open: boolean;
  onClose: () => void;
  notificationRouter: NotificationRouterHandle;
  onOpenSettings: () => void;
};

const Row = ({
  notification,
  group,
  busy,
  onSelect,
  onDismiss,
}: {
  notification: NotificationRecord;
  group: NotificationGroup;
  busy: boolean;
  onSelect: () => void;
  onDismiss: () => void;
}) => {
  const { t } = useNls();
  const ref = useRef<HTMLDivElement>(null);
  const dragControls = useDragControls();
  const x = useMotionValue(0);
  const opacity = useTransform(x, [-180, -24], [1, 0]);
  const dragged = useRef(false);
  return (
    <div ref={ref} className="memberNotificationRowWrap">
      <motion.div className="memberNotificationDismiss" style={{ opacity }}>
        <DeleteRounded />
        <span>{t('NLSMemberNotificationDismiss')}</span>
      </motion.div>
      <MotionPressable
        drag={busy ? false : 'x'}
        dragControls={dragControls}
        dragConstraints={{ left: -320, right: 0 }}
        dragElastic={0.08}
        style={{ x }}
        onPointerDown={(event) => {
          if (!busy) dragControls.start(event);
        }}
        onDragStart={() => {
          dragged.current = true;
        }}
        onDragEnd={(_, info) => {
          if (Math.abs(info.offset.x) >= (ref.current?.getBoundingClientRect().width ?? 1) * 0.4) onDismiss();
          else x.set(0);
          window.setTimeout(() => {
            dragged.current = false;
          }, 0);
        }}
        onClick={() => {
          if (!dragged.current && !busy) onSelect();
        }}
        disabled={busy}
        className={`memberNotificationRow ${notification.readAt ? '' : 'isUnread'}`}
      >
        <span className="memberNotificationGlyph">
          <GroupIcon group={group} category={notification.category} />
          {!notification.readAt ? <i /> : null}
        </span>
        <span className="memberNotificationCopy">
          <span className="memberNotificationTitle">{notification.title}</span>
          <span className="memberNotificationWorkspace text-[11px] text-[var(--muted)]">
            {notification.workspaceId ? (notification.workspaceName || notification.workspaceId) : t('NLSNotificationRowAccount')}
          </span>
          {notification.body && notification.category !== 'DUE' ? (
            <span className="memberNotificationBody">{notification.body}</span>
          ) : null}
        </span>
        <span className="memberNotificationMeta">
          {formatRelative(notification.createdAt)}
          <ChevronRightRounded />
        </span>
      </MotionPressable>
    </div>
  );
};

export const MemberNotificationDrawer = ({ open, onClose, notificationRouter, onOpenSettings }: Props) => {
  usePushBackHandler(onClose, open);
  const { notifications, markRead, clearNotification, clearAll, removeDigestItem, restoreNotifications } = useNotifications();
  const { runWithGlobalLoading } = useGlobalActionLoading();
  const { showToast } = useToast();
  const { t } = useNls();
  const groupMeta: Record<NotificationGroup, { label: string; eyebrow: string }> = {
    ACTIVITY: { label: t('NLSMemberNotificationGroupActivity'), eyebrow: t('NLSMemberNotificationGroupActivityEyebrow') },
    AI: { label: t('NLSMemberNotificationGroupAi'), eyebrow: t('NLSMemberNotificationGroupAiEyebrow') },
    REWARDS: { label: t('NLSMemberNotificationGroupRewards'), eyebrow: t('NLSMemberNotificationGroupRewardsEyebrow') },
    SYSTEM: { label: t('NLSMemberNotificationGroupSystem'), eyebrow: t('NLSMemberNotificationGroupSystemEyebrow') },
  };
  const portalTarget = typeof document === 'undefined' ? null : document.body;
  const [undo, setUndo] = useState<UndoState | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [expandedDigestId, setExpandedDigestId] = useState<string | null>(null);
  const currentDigest = useMemo(
    () => notifications.find((notification) => notification.id === expandedDigestId),
    [expandedDigestId, notifications],
  );
  useEffect(() => {
    if (!expandedDigestId || currentDigest) return;
    const timer = window.setTimeout(() => setExpandedDigestId(null), 0);
    return () => window.clearTimeout(timer);
  }, [currentDigest, expandedDigestId]);
  const grouped = useMemo(() => {
    const buckets: Record<NotificationGroup, NotificationRecord[]> = {
      ACTIVITY: [],
      AI: [],
      REWARDS: [],
      SYSTEM: [],
    };
    notifications.forEach((entry) => buckets[resolveGroup(entry)].push(entry));
    return buckets;
  }, [notifications]);
  const clearEntries = async (entries: NotificationRecord[], message: string) => {
    await Promise.all(entries.map((entry) => clearNotification(entry.id)));
    setUndo({ ids: entries.map((entry) => entry.id), message });
  };
  const dismiss = async (notification: NotificationRecord) => {
    setBusyId(notification.id);
    try {
      await clearEntries([notification], t('NLSMemberNotificationCleared'));
    } finally {
      setBusyId(null);
    }
  };
  const select = async (notification: NotificationRecord) => {
    if (notification.eventType === 'DIGEST' && (notification.digestCount ?? 0) > 1) {
      setExpandedDigestId(notification.id);
      return;
    }
    setBusyId(notification.id);
    try {
      await runWithGlobalLoading(
        async (signal) => {
          const result = notificationRouter.resolveNotification(notification);
          if (result.ok) {
            result.action();
            onClose();
            if (!signal.aborted) {
              if (!notification.readAt) await markRead(notification.id).catch(() => undefined);
            }
            return;
          }
          const message = notification.body
            ? `${notification.title}\n${notification.body}`
            : notification.title;
          showToast(message, result.reason === 'digest-expand' ? 'info' : 'warning');
          if (!notification.readAt) await markRead(notification.id).catch(() => undefined);
          await clearNotification(notification.id).catch(() => undefined);
          setUndo({
            ids: [notification.id],
            message: t('NLSMemberNotificationCleared'),
          });
          onClose();
        },
        {
          label: t('NLSMemberNotificationOpening'),
          messages: [t('NLSMemberNotificationOpeningOverlay'), t('NLSMemberNotificationMarkingRead'), t('NLSMemberNotificationPreventingRepeat')],
        },
      );
    } finally {
      setBusyId(null);
    }
  };
  const selectDigestItem = async (digestId: string, item: DigestInnerItem, index: number) => {
    const result = notificationRouter.resolveDigestItem(item);
    if (result.ok) {
      result.action();
      onClose();
      await removeDigestItem(digestId, index).catch(() => undefined);
      return;
    }
    showToast(item.body ? `${item.title}\n${item.body}` : item.title, result.reason === 'stale-deeplink' ? 'warning' : 'info');
    await removeDigestItem(digestId, index).catch(() => undefined);
    setUndo({ ids: [digestId], message: `${item.title} — cleared (couldn't open)` });
  };
  const restore = async () => {
    const current = undo;
    setUndo(null);
    if (current) await restoreNotifications(current.ids).catch(() => undefined);
  };
  const confirmClear = async () => {
    setConfirmBusy(true);
    try {
      await clearAll();
      setConfirmOpen(false);
    } finally {
      setConfirmBusy(false);
    }
  };
  const drawer = (
    <AnimatePresence>
      {open ? (
        <>
          <MotionPressable
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="memberNotificationScrim"
            onClick={onClose}
            aria-label={t('NLSMemberNotificationClose')}
          />
          {/* eslint-disable-next-line no-restricted-syntax -- member notification slide-in drawer uses custom spring motion and is not a Common modal */}
          <motion.aside role="dialog"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 280, damping: 30 }}
            className="memberNotificationDrawer"
            aria-label={t('NLSMemberNotificationTitle')}
            data-testid="member-notification-drawer"
          >
            <header className="memberNotificationHeader">
              <p>{t('NLSMemberNotificationTitle')}</p>
              {notifications.length ? (
                <Button variant="secondary" size="sm" onClick={() => (notifications.length > 5 ? setConfirmOpen(true) : void clearEntries(notifications, t('NLSMemberNotificationsCleared')))}>
                  {t('NLSMemberNotificationClearAll')}
                </Button>
              ) : null}
            </header>
            <div className="memberNotificationList">
              {currentDigest ? (
                <section className="memberDigestExpansion" aria-label={currentDigest.title}>
                  <header className="memberDigestExpansionHeader">
                    <Button variant="secondary" size="sm" onClick={() => setExpandedDigestId(null)}>
                      <ArrowBackRounded />
                      {t('NLSMemberNotificationBack')}
                    </Button>
                    <strong>{currentDigest.title}</strong>
                  </header>
                  {currentDigest.digestItems?.map((item, index) => {
                    const innerNotification: NotificationRecord = {
                      ...currentDigest,
                      id: `${currentDigest.id}-${index}`,
                      title: item.title,
                      body: item.body,
                      category: item.category ?? currentDigest.category,
                    };
                    return (
                      <Row
                        key={`${currentDigest.id}-${index}`}
                        notification={innerNotification}
                        group={resolveGroup(innerNotification)}
                        busy={busyId === innerNotification.id}
                        onSelect={() => void selectDigestItem(currentDigest.id, item, index)}
                        onDismiss={() => void removeDigestItem(currentDigest.id, index)}
                      />
                    );
                  })}
                </section>
              ) : notifications.length === 0 ? (
                <div className="memberNotificationEmpty">
                  <TaskAltRounded />
                  <p>{t('NLSMemberNotificationEmpty')}</p>
                </div>
              ) : (
                GROUP_ORDER.map((group) => {
                  const entries = grouped[group];
                  const unreadCount = entries.filter((entry) => !entry.readAt).length;
                  return entries.length ? (
                    <section key={group}>
                      <header className="memberNotificationGroupHeader">
                        <span>
                          <strong>{groupMeta[group].label}</strong>
                          <small>{groupMeta[group].eyebrow}</small>
                        </span>
                        <span className="memberNotificationGroupActions">
                          {unreadCount > 0 ? <i>{unreadCount}</i> : null}
                          <Button variant="secondary" size="sm" onClick={() => void clearEntries(entries, `${groupMeta[group].label} ${t('NLSMemberNotificationCleared').toLowerCase()}`)}>
                            {t('NLSMemberNotificationClear')}
                          </Button>
                        </span>
                      </header>
                      {entries.map((notification) => (
                        <Row
                          key={notification.id}
                          notification={notification}
                          group={group}
                          busy={busyId === notification.id}
                          onSelect={() => void select(notification)}
                          onDismiss={() => void dismiss(notification)}
                        />
                      ))}
                    </section>
                  ) : null;
                })
              )}
            </div>
            <footer className="memberNotificationFooter">
              <Button variant="secondary" size="sm" onClick={onOpenSettings}>
                <SettingsRounded />
                {t('NLSMemberNotificationSettings')}
              </Button>
            </footer>
            <NotificationUndoSnackbar undo={undo} onUndo={() => void restore()} onDismiss={() => setUndo(null)} />
          </motion.aside>
          <ConfirmActionModal
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={() => void confirmClear()}
            title={t('NLSMemberNotificationConfirmTitle')}
            body={t('NLSMemberNotificationConfirmBody')}
            confirmLabel={t('NLSMemberNotificationClearAll')}
            danger
            busy={confirmBusy}
          />
        </>
      ) : null}
    </AnimatePresence>
  );
  return portalTarget ? createPortal(drawer, portalTarget) : drawer;
};
